import { HiOutlineCheck, HiPlus, HiTrash } from 'react-icons/hi';
import {
  FormProvider,
  UseFieldArrayRemove,
  useFormContext,
  useForm,
  useFieldArray,
  useController,
} from 'react-hook-form';
import { toast } from 'react-hot-toast';
import Card from '../../../components/Card';
import IconButton from '../../../components/IconButton';
import Textarea from '../../../components/Textarea';
import { useUserSession } from '../../../components/UserSession';
import Page from '../../../layouts/DashboardLayout/Page';
import styles from './Templates.module.scss';
import { useCallback, useEffect, useRef } from 'react';
import { getPatch } from '../../../utils';
import {
  useTemplateControllerCreateTemplates,
  useTemplateControllerDeleteTemplates,
  useTemplateControllerGetTemplates,
  useTemplateControllerUpdateTemplates,
} from '../../../api/informApi/informApiComponents';
import { SerializedTemplate } from '../../../api/informApi/informApiSchemas';
import LabelEditor from '../../../components/LabelEditor';

interface TemplateProps {
  remove: UseFieldArrayRemove;
  index: number;
}
function Template({ index, remove }: TemplateProps) {
  const { mutateAsync: deleteTemplate, isLoading: isDeleting } =
    useTemplateControllerDeleteTemplates();
  const { register, getValues } = useFormContext();
  const labelController = useController({
    name: `templates.${index}.name`,
  });
  const userCtx = useUserSession();

  return (
    <Card padding="1.5rem 2.25rem" backgroundColor="background-1" as="li">
      <div className={styles.header}>
        <LabelEditor
          {...labelController.field}
          textProps={{
            textType: 'p1',
          }}
        />
        <IconButton
          onClick={() => {
            toast
              .promise(
                deleteTemplate({
                  pathParams: {
                    id: userCtx.currentOrganization!,
                  },
                  body: {
                    items: [
                      {
                        templateId: getValues(`templates.${index}.id`),
                      },
                    ],
                  },
                }),
                {
                  loading: 'Deleting template...',
                  success: 'Template deleted!',
                  error: 'Unable to delete template',
                },
              )
              .then(() => remove(index));
          }}
          disabled={isDeleting}
          buttonColor="error"
          variant="solid"
          icon={HiTrash}
        />
      </div>
      <Textarea
        {...register(`templates.${index}.content`)}
        className={styles.textarea}
        disabled={isDeleting}
        rows={10}
      />
    </Card>
  );
}

function Templates() {
  const userCtx = useUserSession();
  const initState = useRef<SerializedTemplate[]>();
  const { mutateAsync: createTemplate, isLoading: isCreating } =
    useTemplateControllerCreateTemplates();
  const { mutateAsync: updateTemplates, isLoading: isUpdating } =
    useTemplateControllerUpdateTemplates();
  const { data, isLoading } = useTemplateControllerGetTemplates(
    {
      pathParams: { id: userCtx.currentOrganization! },
    },
    {
      enabled: !!userCtx.currentOrganization,
    },
  );
  const formCtx = useForm<{ templates: SerializedTemplate[] }>({
    defaultValues: { templates: [] },
  });
  const { control, watch, reset: _reset } = formCtx;
  const { append, remove } = useFieldArray({
    name: 'templates',
    control,
  });

  const reset = useCallback(
    (data: SerializedTemplate[] = watch('templates')) => {
      initState.current = data?.map((item) => ({ ...item })) ?? [];
      _reset({ templates: initState.current });
    },
    [_reset, watch],
  );

  // Syncs data with form
  useEffect(() => {
    if (!isLoading) reset(data?.items);
  }, [isLoading, data, reset]);

  // Updates data when values are changed
  const fields = watch('templates');
  const changes = getPatch(initState.current ?? [], fields ?? [], (i) => i?.id);

  return (
    <Page
      heading="Templates"
      description="Templates will be used as replies when linked to a rule."
      actions={[
        {
          async onClick() {
            toast
              .promise(
                createTemplate({
                  pathParams: {
                    id: userCtx.currentOrganization!,
                  },
                  body: {
                    items: [
                      {
                        name: 'New Template',
                        content: '',
                      },
                    ],
                  },
                }),
                {
                  loading: 'Creating template...',
                  success: 'Template created!',
                  error: 'Unable to create template',
                },
              )
              .then((template) => {
                append(template);
                reset();
              });
          },
          children: 'Create Template',
          buttonColor: 'success',
          variant: 'outline',
          disabled: isCreating,
          icon: {
            placement: 'left',
            element: HiPlus,
          },
        },
        {
          async onClick() {
            toast
              .promise(
                updateTemplates({
                  pathParams: { id: userCtx.currentOrganization! },
                  body: {
                    items: changes.update.map((item) => ({
                      content: item.content ?? '',
                      name: item.name ?? '',
                      templateId: item.id!,
                    })),
                  },
                }),
                {
                  loading: 'Updating templates...',
                  success: 'Templates updated!',
                  error: 'Unable to update templates',
                },
              )
              .then(() => reset());
          },
          children: 'Save Changes',
          buttonColor: 'success',
          variant: 'solid',
          disabled: isUpdating || !changes.update.length,
          icon: {
            placement: 'left',
            element: HiOutlineCheck,
          },
        },
      ]}
      loading={(isLoading && !data) || userCtx.isAuthenticating}
    >
      <FormProvider {...formCtx}>
        <ul className={styles.items}>
          {fields.map((item, index) => (
            <Template index={index} remove={remove} key={item.id} />
          ))}
        </ul>
      </FormProvider>
    </Page>
  );
}

Templates.routeProps = {
  path: '/templates',
};
export default Templates;
